import { z, type ZodType } from '@customerio/hydra-validation';
import type { ObjectType as UIAPIObjectType } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * ObjectType entity — workspace-scoped object-type metadata (segments,
 * journeys, snippets, etc. can target specific object types).
 *
 * Backend at `/v1/environments/:environment_id/object_types`
 * (`listObjectTypes` in `services/ui_api/objects/list_types.go`). No
 * `created`/`updated` on the wire. Reads live in `queries.ts`;
 * create/update/delete live in `mutations.ts`.
 *
 * **`id` is `string`** per the layer convention even though the wire
 * encodes it as `number` — same Tag/SubscriptionTopic/Snippet coercion.
 */

export type ObjectType = Omit<UIAPIObjectType, 'id'> & {
  id: string;
};

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const ObjectTypeFromApi = z.looseObject({
  id: z.number().transform(String),
}) as unknown as ZodType<ObjectType, unknown>;

/** @public */
export const objectType = defineEntity<ObjectType, ObjectType, void, undefined, 'workspace'>({
  name: 'object_type',
  scope: 'workspace',
  pk: (t) => t.id,
  schema: { read: ObjectTypeFromApi },
});

export const objectTypeKeys = objectType.keys;

export function parseObjectType(raw: UIAPIObjectType): ObjectType {
  return objectType.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions                         */
/* ------------------------------------------------------------------------- */

/**
 * Direct port of the Ember `ObjectTypeModel#objectIcon` getter — falls
 * back to the generic `'object'` icon when the type has no custom one.
 *
 * Returned as a plain string rather than typed against `IconName` so
 * this util doesn't have to take a dependency on `@customerio/pluma-icons`;
 * call sites that pass this into a Pluma `<Icon name=…>` prop can cast.
 */
/** @public */
export function objectTypeIcon(type: Pick<ObjectType, 'icon'>): string {
  return type.icon != null && type.icon !== '' ? type.icon : 'object';
}
